import { strongActionVerbs } from "./scorer-strong-verbs";
import sanitizeHtml from "sanitize-html";
import { isValidUrl } from "./helpers";

export const typesOfExperiences = [
    "work_experience",
    "leadership_experience",
    "project_experience",
];

export const skipJobTitles = ["intern", "volunteer", "member"];

export const WARNING_TYPES = {
    bullets: "bullet",
    format: "formatting",
    info: "information",
    skills: "skills",
};

export const WARNING_CATEGORIES = {
    strongVerbs: "strong-verbs",
    keyWords: "key-words",
};

export const CATEGORY_STYLE = {
    [WARNING_TYPES.bullets]: "bg-won-sky-blue",
    [WARNING_TYPES.format]: "bg-won-purple",
    [WARNING_TYPES.info]: "bg-won-orange",
    [WARNING_TYPES.skills]: "bg-won-yellow",
};

export const MESSAGES_TEMPLATES = {
    strongActionVerbs: `Try to change "<<verb>>" with <<proposedVerbs>>`,
    keyWords: `Add more relevant keywords to your job description. Try to add <<keywords>>`,
};

export const scoreBreakDownMessage = {
    fullyMet: "Fully met",
    partiallyMet: "Partially met",
    notMet: "Not met",
};

export const score = {
    bulletPointContent: {
        lengthFactor: 16,
        statisticsFactor: 16,
        actionVerbsFactor: 18,
    },
    formatting: {
        sectionFormat: 2.5,
        experienceOrder: 2.5,
        readAbility: 2.5,
        margins: 2.5,
    },
    relevantInformation: {
        personalInformation: 5,
        hasUrl: 5,
        datesForExperiences: 5,
        bulletPoints: 5,
        educationInfo: 2.5,
        experienceInfo: 2.5,
    },
    skills: 15,
};

export const generateRandomId = (length = 10) => {
    return Math.random().toString(36).substring(2, length);
};

export const isDate = (str) => {
    const testDate = new Date(str);
    return !isNaN(testDate) && testDate instanceof Date;
};

export const isDateArrayInReverseChronologicalOrder = (dateArray) => {
    // Single date or empty array is considered chronological
    if (dateArray.length <= 1) {
        return true;
    }

    let previousDate =
        dateArray[0] === "Present" ? new Date() : new Date(dateArray[0]);
    let isReverseChronological = true;

    dateArray.slice(1).forEach((dateString) => {
        const currentDate =
            dateString === "Present" ? new Date() : new Date(dateString);

        if (isNaN(currentDate)) {
            isReverseChronological = false;
            return;
        }

        if (currentDate > previousDate) {
            isReverseChronological = false;
        }
        previousDate = currentDate;
    });

    return isReverseChronological;
};

export const addSkills = (
    itemId,
    matchedKeywords,
    proposedKeywords,
    allKeywords,
    refreshProposeKeywords
) => {
    return {
        allKeywords,
        proposedKeywords,
        warningId: `add-more-bp-${itemId}`,
        warning: "ADD KEYWORDS",
        warningType: WARNING_TYPES.skills,
        warningCategory: WARNING_CATEGORIES.keyWords,
        refresh: refreshProposeKeywords,
        shortMessage: `Add ${5 - matchedKeywords.length} more keyword${
            matchedKeywords.length === 4 ? "" : "s"
        }`,
        detailedMessage: MESSAGES_TEMPLATES.keyWords.replace(
            "<<keywords>>",
            proposedKeywords.join(", ")
        ),
    };
};

export const addExperience = () => {
    return {
        warningId: `add-experience`,
        warning: "MISSING EXPERIENCE SECTIONS",
        warningType: WARNING_TYPES.format,
        shortMessage: `Add work, project or leadership experiences`,
        detailedMessage:
            "To catch a recruiter's eye, populate your resume with work, project, or leadership experiences",
    };
};

export const addName = () => {
    return {
        warningId: "add-name",
        type: "relevantInfo-personalInfoName",
        warningType: WARNING_TYPES.info,
        warning: "PERSONAL INFO",
        shortMessage: "Add name",
        detailedMessage:
            "Make sure to include your name so recruiters know who you are",
    };
};

export const addPhoneNumber = () => {
    return {
        warningId: "add-contact",
        type: "relevantInfo-personalInfoPhone",
        warningType: WARNING_TYPES.info,
        warning: "PERSONAL INFO",
        shortMessage: "Add phone number",
        detailedMessage:
            "Make sure to include your phone number so recruiters can contact you",
    };
};

export const addEmail = () => {
    return {
        warningId: "add-contact",
        type: "relevantInfo-personalInfoEmail",
        warningType: WARNING_TYPES.info,
        warning: "PERSONAL INFO",
        shortMessage: "Add email",
        detailedMessage:
            "Make sure to include your email so recruiters can contact you",
    };
};

export const addUrl = () => {
    return {
        warningId: "add-contact",
        type: "relevantInfo-personalInfoUrl",
        warningType: WARNING_TYPES.info,
        warning: "PERSONAL INFO",
        shortMessage: "Missing URL",
        detailedMessage:
            "Make sure to include a URL (e.g. LinkedIn, portfolio) to provide more information to recruiters",
    };
};

export const addDatesToExperiences = (itemsId) => {
    return {
        id: itemsId,
        warningId: `experience-date-${itemsId}`,
        warningType: WARNING_TYPES.info,
        type: "relevantInfo-experiencesDate",
        warning: "MISSING DATE",
        shortMessage: "Add a date",
        detailedMessage: "Make sure to add dates to all of your experiences",
    };
};

export const addDatesToEducation = (itemsId) => {
    return {
        id: itemsId,
        warningId: `experience-date-${itemsId}`,
        warningType: WARNING_TYPES.info,
        type: "relevantInfo-experiencesDate",
        warning: "MISSING DATE",
        shortMessage: "Add or remove date",
        detailedMessage:
            "Make sure to add or remove dates for all of your educations",
    };
};

export const addSectionOrder = (sectionId, userType) => {
    return {
        id: sectionId,
        warningId: sectionId ? `section-${sectionId}` : null,
        type: "formatting-sectionFotmat",
        warningType: WARNING_TYPES.format,
        warning: "FOLLOW STANDARD FORMAT",
        shortMessage: "Reorder sections",
        detailedMessage: `${
            userType === "Students"
                ? "Students should have their education at the top of their resume"
                : "Those who are not students should have their work experience at the top of their resume"
        }`,
    };
};

export const addChronologicalOrder = (sectionId) => {
    return {
        sectionId,
        warningId: `chronological-order-${sectionId}`,
        warningType: WARNING_TYPES.format,
        type: "formatting-dates",
        warning: "FOLLOW STANDARD FORMAT",
        shortMessage: "Reorder experiences",
        detailedMessage:
            "Experiences should be ordered from most to least current",
    };
};

export const increaseFontSize = () => {
    return {
        warningId: `increase-font`,
        type: "formatting-increaseFontSize",
        warningType: WARNING_TYPES.format,
        warning: "ENSURE READABILITY",
        shortMessage: "Increase font size",
        detailedMessage:
            "Your font size looks a little too small, we suggest sizes between 11-14pt font",
    };
};

export const decreaseFontSize = () => {
    return {
        warningId: `decrease-font`,
        type: "formatting-decreaseFontSize",
        warning: "ENSURE READABILITY",
        shortMessage: "Decrease font size",
        detailedMessage:
            "Your font size looks a little too big, we suggest sizes between 11-14pt font",
    };
};

export const resumePagesExceeded = () => {
    return {
        warningId: `pages-nr-exceeded`,
        type: "formatting-nrOfPagesExceeded",
        warningType: WARNING_TYPES.format,
        warning: "ADJUST PAGE FORMAT",
        shortMessage: "Fit resume in 1 or 2 pages",
        detailedMessage:
            "Try to fit your resume in one page. Resumes should typically be only one page, and at most 2 pages",
    };
};

export const addMoreWordsBulletPoint = (point) => {
    return {
        type: "bp-details",
        warningId: point.warningId,
        warningType: WARNING_TYPES.bullets,
        warning: "ADD DETAIL",
        shortMessage: "Add more words",
        detailedMessage:
            "This bullet point needs more detail. Each bullet point should be more than 12 words long",
    };
};

export const addMoreMetricsBulletPoint = (
    totalBpAmount,
    bpWithMetrics,
    metricsNeeded
) => {
    return {
        type: "bp-metrics",
        warningId: `highlight-bp-metrics`,
        warningType: WARNING_TYPES.bullets,
        warning: "ADD METRICS",
        shortMessage: "Quantify Your Achievements",
        detailedMessage: `Your resume lists ${totalBpAmount} bullet point${
            totalBpAmount === 1 ? "" : "s"
        }, but ${
            bpWithMetrics === 0 ? "none" : "only " + bpWithMetrics
        } has statistics. Add numbers to at least ${metricsNeeded} more bullet${
            metricsNeeded === 1 ? "" : "s for a robust, results-driven resume"
        }.`,
    };
};

export const addActionVerbsBulletPoint = (point) => {
    return {
        type: "bp-verbs",
        warningId: point.warningId,
        warningType: WARNING_TYPES.bullets,
        warning: "ADD ACTION VERBS",
        shortMessage: "Add action verbs",
        detailedMessage:
            "This bullet point has no action verbs. Action verbs leave an impactful impression on recruiters, try to include at least 1 action verb per bullet",
    };
};

export const addStrongActionVerbsBulletPoint = (
    point,
    verb,
    strongVerbsArr,
    getStrongActionVerbs
) => {
    return {
        verb,
        refresh: getStrongActionVerbs,
        type: "bp-strong-verbs",
        warningId: point.warningId,
        warningType: WARNING_TYPES.bullets,
        warningCategory: WARNING_CATEGORIES.strongVerbs,
        warning: "ADD STRONG ACTION VERBS",
        shortMessage: `Use strong action verbs instead of "${verb}"`,
        detailedMessage: MESSAGES_TEMPLATES.strongActionVerbs
            .replace("<<verb>>", verb)
            .replace("<<proposedVerbs>>", strongVerbsArr.join(", ")),
    };
};

export const addRelevantInfoBulletPoints = (sectionId, missingPoints) => {
    return {
        sectionId,
        warningId: `add-more-bp-${sectionId}`,
        type: "relevantInfo-bp",
        warning: "MISSING BULLET POINTS",
        warningType: WARNING_TYPES.info,
        shortMessage: `Add ${missingPoints} more bullet point${
            missingPoints === 1 ? "" : "s"
        }`,
        detailedMessage: `Try to add ${missingPoints} more bullet point${
            missingPoints === 1 ? "" : "s"
        }. Each experience should have at least 3 bullet points`,
    };
};

export const adjustMargins = () => {
    return {
        warningId: "adjust-margins",
        type: "formatting-margins",
        warningType: WARNING_TYPES.format,
        warning: "FOLLOW STANDARD FORMAT",
        shortMessage: "Adjust margins",
        detailedMessage: "Ensure that both margins are equal",
    };
};

export const jobTitleIsRare = (sectionId, itemId, jobTitle) => {
    return {
        warningId: `add-work-experience-title-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.skills,
        warning: "JOB TOO RARE",
        shortMessage: `Job title '${jobTitle}' is too rare`,
        detailedMessage: `Job title '${jobTitle}' is too rare for analysis. Please choose another`,
    };
};

export const isAnIntern = (sectionId, itemId) => {
    return {
        warningId: `add-work-experience-title-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.skills,
        warning: "CHOOSE ANOTHER JOB TITLE",
        shortMessage: `Specify a more detailed job title`,
        detailedMessage: `Try to not use 'Intern', 'Volunteer' or 'Member' as a job title. Please choose another`,
    };
};

export const notAJobTitle = (sectionId, itemId, jobTitle) => {
    return {
        warningId: `add-work-experience-title-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.skills,
        warning: "NOT A JOB TITLE",
        shortMessage: `'${jobTitle}' is not a job title`,
        detailedMessage: `'${jobTitle}' is not intended for specifying your job title. Please use the appropriate section`,
    };
};

export const addEducation = () => {
    return {
        warningId: "add-education-section",
        warningType: WARNING_TYPES.info,
        warning: "ADD EDUCATION",
        shortMessage: "Missing education section",
        detailedMessage:
            "Your resume currently lacks an education section. Make sure to include this to give recruiters a sense of your educational background",
    };
};

export const keepOneEducation = () => {
    return {
        warningType: WARNING_TYPES.info,
        warning: "ADD ONLY ONE EDUCATION",
        shortMessage: `Multiple education sections`,
        detailedMessage: `Your resume has more than one section labeled as 'Education'. Consolidate them into a single section for better readability`,
    };
};

export const addDegree = (sectionId, itemId) => {
    return {
        warningId: `add-education-degree-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD DEGREE",
        shortMessage: `Missing degree information`,
        detailedMessage: `Include your degree (e.g., B.S. in Computer Science, MBA) in the education section to clearly state your educational qualifications`,
    };
};

export const addLocation = (sectionId, itemId) => {
    return {
        warningId: `add-education-location-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD LOCATION",
        shortMessage: `Missing university location`,
        detailedMessage: `Include the location of your university (City, State) to provide additional context for recruiters`,
    };
};

export const addUniversity = (sectionId, itemId) => {
    return {
        warningId: `add-education-university-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD UNIVERSITY",
        shortMessage: `Missing university name`,
        detailedMessage: `Don't forget to mention the university where you earned your degree. This gives more context to your educational background`,
    };
};

export const addWorkExperienceJobTitle = (sectionId, itemId) => {
    return {
        warningId: `add-work-experience-title-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD JOB TITLE",
        shortMessage: `Missing job title`,
        detailedMessage: `Don't forget to list your job title to specify your role and responsibilities at the company`,
    };
};

export const addWorkExperienceCompany = (sectionId, itemId) => {
    return {
        warningId: `add-work-experience-organization-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD COMPANY",
        shortMessage: `Missing company name`,
        detailedMessage: `Include the name of the company where you've worked to give recruiters context about your experience`,
    };
};

export const addWorkExperienceCity = (sectionId, itemId) => {
    return {
        warningId: `add-work-experience-city-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD CITY",
        shortMessage: `Missing work location`,
        detailedMessage: `Include the city where you worked to provide geographical context to your work experience`,
    };
};

export const addLeadershipOrganization = (sectionId, itemId) => {
    return {
        warningId: `add-leadership-organization-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD ORGANIZATION",
        shortMessage: `Missing organization name`,
        detailedMessage: `List the name of the organization you were involved with to give recruiters context on where you gained your experience`,
    };
};

export const addLeadershipTitle = (sectionId, itemId) => {
    return {
        warningId: `add-leadership-title-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD TITLE",
        shortMessage: `Missing leadership title`,
        detailedMessage: `Specify your title or role in the project or organization to clearly outline your responsibilities`,
    };
};

export const addLeadershipCity = (sectionId, itemId) => {
    return {
        warningId: `add-leadership-city-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD LOCATION",
        shortMessage: `Missing location for leadership`,
        detailedMessage: `Include the city where your leadership experience took place for additional context`,
    };
};

export const addProjectOrganization = (sectionId, itemId) => {
    return {
        warningId: `add-project-experience-organization-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD ORGANIZATION",
        shortMessage: `Missing organization name`,
        detailedMessage: `List the name of the organization you were involved with to give recruiters context on where you gained your experience`,
    };
};

export const addProjectTitle = (sectionId, itemId) => {
    return {
        warningId: `add-project-experience-title-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD TITLE",
        shortMessage: `Missing project title`,
        detailedMessage: `List the name of the organization you were involved with to give recruiters context on where you gained your experience`,
    };
};

export const addProjectCity = (sectionId, itemId) => {
    return {
        warningId: `add-project-experience-city-${sectionId}-${itemId}`,
        warningType: WARNING_TYPES.info,
        warning: "ADD PROJECT LOCATION",
        shortMessage: `Missing location for project`,
        detailedMessage: `Include the city where your project experience took place for additional context`,
    };
};

export const getNumberOfWords = (text) => {
    return text.trim().split(" ").length;
};

export const hasPhoneNumber = (text) => {
    const phoneRegex = /(\+\d+)?(\d\D*){9,}/;

    return phoneRegex.test(removeHTMLTags(text));
};

export const hasEmail = (text) => {
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;

    return emailRegex.test(text);
};

export const hasURL = (text) => {
    return isValidUrl(text);
};

export const hasNumbers = (text) => {
    const simpleNumberPattern = /\b\d+\b/g;

    const wordNumberPattern =
        /\b(zero|one|two|three|four|five|six|seven|eight|nine|ten)\b/gi;

    return simpleNumberPattern.test(text) || wordNumberPattern.test(text);
};

export const hasActionVerbs = (text) => {
    // define the pattern for words that end with 'ed', 'ing', 's'
    if (typeof text !== 'string' || text.trim() === '') return false;
    
    const actionVerbPattern = /\b\w+(?:ed|ing)\b/g;
    const firstFourWords = text.split(/\s+/).slice(0, 4);

    return firstFourWords.some((word) =>
        actionVerbPattern.test(word)
    );
};

const getActionVerbs = (text) => {
    // Define the pattern for words that end with 'ed', 'ing', 's'
    const actionVerbPattern = /\b\w+(?:ed|ing)\b/g;
    const firstFourWords = text.split(/\s+/).slice(0, 4);

    // Match first 4 words in the text that match the action verb pattern
    const actionVerbsArray = firstFourWords.filter((word) =>
        actionVerbPattern.test(word)
    );

    if (!actionVerbsArray || actionVerbsArray.length === 0) {
        return [];
    }

    return actionVerbsArray;
};

export const getActionVerbTense = (actionVerb) => {
    const wordEndsWithIngRegex = /\b\w+ing\b/;
    const wordEndsWithEdRegex = /\b\w+ed\b/;
    
    if (wordEndsWithIngRegex.test(actionVerb)) {
        return "Current";
    } else if (wordEndsWithEdRegex.test(actionVerb)) {
        return "Past";
    }

    return;
};

export const getScorePerFactor = (factorScore, factors) => {
    const result = (factorScore / factors).toFixed(2);

    return parseFloat(result);
};

export const getNotStrongActionVerbs = (actionVerbsArray) => {
    const notStrongActionVerbs = [];

    actionVerbsArray.forEach((verb) => {
        let strongVerbFound = false;

        strongActionVerbs.forEach((strongVerbs) => {
            const strongVerbsValues = Object.values(strongVerbs);
            if (strongVerbsValues.includes(verb)) {
                strongVerbFound = true;
                return;
            }
        });

        if (!strongVerbFound) {
            notStrongActionVerbs.push(verb);
        }
    });

    return notStrongActionVerbs;
};

const isActionVerbStrong = (actionVerb) => {
    const formattedActionVerb = actionVerb.toLowerCase();

    for (let i = 0; i < strongActionVerbs.length; i++) {
        let strongVerbsValues = Object.values(strongActionVerbs[i]);

        // to lower case
        strongVerbsValues = strongVerbsValues.map((str) => str.toLowerCase().trim());

        if (strongVerbsValues.includes(formattedActionVerb)) {
            return true;
        }
    }
    
    return false;
};

const getDifferentActionVerbFrom = (verb, tense) => {
    const random = Math.floor(Math.random() * strongActionVerbs.length);
    let verbObject = strongActionVerbs[random];
    
    if(verbObject[tense].toLowerCase() === verb.toLowerCase()) {
        return getDifferentActionVerbFrom(verb, tense)
    }
    
    return verbObject
}

const getRandomStrongWords = (tense, verb) => {
    const verbsArray = [];

    for (let i = 0; i < 3; i++) {
        const verbObject = getDifferentActionVerbFrom(verb, tense)

        if (tense === "Current") {
            verbsArray.push(verbObject.Current);
        } else if (tense === "Past") {
            verbsArray.push(verbObject.Past);
        } else {
            verbsArray.push(verbObject.Verb);
        }
    }

    return verbsArray;
};

export const proposeStrongActionVerbs = (notStrongActionVerbs) => {
    const strongActionVerbsObject = {};

    notStrongActionVerbs.forEach((verb) => {
        const tense = getActionVerbTense(verb);

        strongActionVerbsObject[verb] = getRandomStrongWords(tense, verb);
    });

    return strongActionVerbsObject;
};

export const getStrongActionVerbs = ({ point }) => {
    const actionVerbsArray = getActionVerbs(point);
    const strongActionVerbsObject = {};
    let newVerbs = [];

    actionVerbsArray.forEach((actionVerb) => {
        const isVerbStrong = isActionVerbStrong(actionVerb);

        if (!isVerbStrong) {
            const tense = getActionVerbTense(actionVerb);
            newVerbs = getRandomStrongWords(tense, actionVerb);

            strongActionVerbsObject[actionVerb] = newVerbs;
            strongActionVerbsObject[actionVerb].tense = tense;
        }
    });

    return { strongActionVerbsObject, verbs: newVerbs };
};

export const checkWordsInText = (text, wordsToCheck) => {
    const formattedText = text.toLowerCase();

    const textWords = formattedText.split(/\s+/);

    const matchedWords = [];

    for (const word of wordsToCheck) {
        if (textWords.includes(word.toLowerCase())) {
            matchedWords.push(word);
        }
    }

    return matchedWords.length;
};

export const removeHTMLTags = (text) => {
    return sanitizeHtml(text, {
        allowedTags: [],
    });
};

export const findKeywords = (inputString, keywordArray) => {
    const inputToLower = inputString.toLowerCase();
    const matchedKeywords = [];

    keywordArray.forEach((keyword) => {
        if (inputToLower.includes(keyword.toLowerCase())) {
            matchedKeywords.push(keyword.toLowerCase());
        }
    });

    return matchedKeywords;
};

// Function to calculate how many bp should have metrics to reach max points
export const calculateNeededMetrics = (totalBpAmount, bpWithMetrics) => {
    return Math.ceil(0.8 * totalBpAmount - bpWithMetrics);
};
